
import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  useForm,
} from 'react-hook-form';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  Loader2,
  Upload,
} from 'lucide-react';

import { useSchool } from '../../../context/SchoolContext';

import { academicApi } from '../../academics/services/academicApi';

import {
  getApiErrorMessage,
  getApiFieldErrors,
  getStudentIdFromResponse,
  studentApi,
} from '../services/studentApi';

import {
  createEmptyStudentValues,
  buildStudentPayload,
  mapStudentDetailToForm,
} from '../utils/studentPayload';

import {
  applyValidationErrors,
  validateStudentForm,
  validationErrorsToSummary,
} from '../utils/studentValidation';

const inputClass = (hasError) =>
  `mt-1 w-full rounded-lg border px-3 py-2.5 text-sm outline-none transition ${
    hasError
      ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-100'
      : 'border-gray-300 bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
  }`;

const getNestedValue = (
  object,
  path,
) =>
  String(path)
    .split('.')
    .reduce(
      (current, key) =>
        current?.[key],
      object,
    );

const getFieldError = (
  errors,
  path,
) =>
  getNestedValue(errors, path)
    ?.message || '';

const RequiredMark = () => (
  <span className="text-red-500">
    {' '}
    *
  </span>
);

const FieldError = ({ message }) => {
  if (!message) {
    return null;
  }

  return (
    <p className="mt-1 text-xs font-medium text-red-600">
      {message}
    </p>
  );
};

const TextInput = ({
  label,
  name,
  register,
  errors,
  required = false,
  type = 'text',
  placeholder = '',
  disabled = false,
  inputMode,
  maxLength,
}) => {
  const error = getFieldError(
    errors,
    name,
  );

  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">
        {label}

        {required && <RequiredMark />}
      </span>

      <input
        type={type}
        inputMode={inputMode}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        className={inputClass(
          Boolean(error),
        )}
        {...register(name)}
      />

      <FieldError message={error} />
    </label>
  );
};

const TextArea = ({
  label,
  name,
  register,
  errors,
  rows = 3,
  placeholder = '',
}) => {
  const error = getFieldError(
    errors,
    name,
  );

  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">
        {label}
      </span>

      <textarea
        rows={rows}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        className={inputClass(
          Boolean(error),
        )}
        {...register(name)}
      />

      <FieldError message={error} />
    </label>
  );
};

const SelectInput = ({
  label,
  name,
  register,
  errors,
  children,
  required = false,
  disabled = false,
  onChange,
}) => {
  const error = getFieldError(
    errors,
    name,
  );

  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700">
        {label}

        {required && <RequiredMark />}
      </span>

      <select
        disabled={disabled}
        aria-invalid={Boolean(error)}
        className={inputClass(
          Boolean(error),
        )}
        {...register(name, {
          onChange,
        })}
      >
        {children}
      </select>

      <FieldError message={error} />
    </label>
  );
};

const AccordionSection = ({
  title,
  description,
  open,
  onToggle,
  hasError = false,
  children,
}) => (
  <section
    className={`mb-5 overflow-hidden rounded-2xl border bg-white shadow-sm ${
      hasError
        ? 'border-red-300'
        : 'border-gray-200'
    }`}
  >
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between bg-gray-50 px-6 py-4 text-left hover:bg-gray-100"
    >
      <div>
        <h2 className="text-lg font-bold text-gray-900">
          {title}
        </h2>

        {description && (
          <p className="mt-1 text-xs text-gray-500">
            {description}
          </p>
        )}
      </div>

      <ChevronDown
        className={`h-5 w-5 shrink-0 text-gray-500 transition-transform ${
          open ? 'rotate-180' : ''
        }`}
      />
    </button>

    {open && (
      <div className="border-t border-gray-200 p-6">
        {children}
      </div>
    )}
  </section>
);

const fileFromValue = (value) => {
  if (!value) {
    return null;
  }

  if (
    typeof File !== 'undefined' &&
    value instanceof File
  ) {
    return value;
  }

  if (
    typeof FileList !== 'undefined' &&
    value instanceof FileList
  ) {
    return value[0] || null;
  }

  if (Array.isArray(value)) {
    return value[0] || null;
  }

  return value?.[0] || null;
};

const AddStudentPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    schoolId,
    academicYears,
    activeAcademicYear,
    activeAcademicYearId,
  } = useSchool();

  const isEditMode = Boolean(id);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: {
      errors,
      isSubmitting,
    },
  } = useForm({
    defaultValues:
      createEmptyStudentValues(),
  });

  const [
    classLevels,
    setClassLevels,
  ] = useState([]);

  const [sections, setSections] =
    useState([]);

  const [
    loadingClasses,
    setLoadingClasses,
  ] = useState(false);

  const [
    loadingSections,
    setLoadingSections,
  ] = useState(false);

  const [pageLoading, setPageLoading] =
    useState(true);

  const [
    errorSummary,
    setErrorSummary,
  ] = useState([]);

  const [
    successMessage,
    setSuccessMessage,
  ] = useState('');

  const [
    warningMessage,
    setWarningMessage,
  ] = useState('');

  const [openSections, setOpenSections] =
    useState({
      student: true,
      academic: true,
      parent: true,
      address: false,
      medical: false,
      previous: false,
      documents: false,
    });

  const selectedAcademicYear = watch(
    'academic_profile.academic_year',
  );

  const selectedClass = watch(
    'academic_profile.class_level',
  );

  const documentValues = watch('docs');

  const selectedAcademicYearName =
    academicYears.find(
      (item) => String(item.id) === String(selectedAcademicYear),
    )?.year_name ||
    activeAcademicYear?.year_name ||
    'Not selected';

  const toggleSection = (section) => {
    setOpenSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const loadClasses = async (
    academicYearId,
  ) => {
    if (
      !schoolId ||
      !academicYearId
    ) {
      setClassLevels([]);
      return [];
    }

    setLoadingClasses(true);

    try {
      const data =
        await academicApi.getClassLevels(
          schoolId,
          {
            academicYearId,
            status: 'ACTIVE',
          },
        );

      setClassLevels(data || []);

      return data || [];
    } finally {
      setLoadingClasses(false);
    }
  };

  const loadSections = async (
    academicYearId,
    classLevelId,
  ) => {
    if (
      !schoolId ||
      !classLevelId
    ) {
      setSections([]);
      return [];
    }

    setLoadingSections(true);

    try {
      const data =
        await academicApi.getSections(
          schoolId,
          {
            academicYearId,
            classLevelId,
            status: 'ACTIVE',
          },
        );

      setSections(data || []);

      return data || [];
    } finally {
      setLoadingSections(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const initializePage = async () => {
      if (!schoolId) {
        setPageLoading(false);
        return;
      }

      setPageLoading(true);
      setErrorSummary([]);

      try {
        if (!isEditMode) {
          const initialValues = createEmptyStudentValues();
          initialValues.academic_profile.academic_year =
            activeAcademicYearId || '';

          if (activeAcademicYearId) {
            await loadClasses(activeAcademicYearId);
          } else {
            setClassLevels([]);
          }

          if (!cancelled) {
            reset(initialValues);
          }
          return;
        }

        const response =
          await studentApi.getStudentById(
            schoolId,
            id,
          );

        const student =
          response?.data ?? response;

        const formValues =
          mapStudentDetailToForm(
            student,
          );

        const academicYearId =
          formValues.academic_profile
            .academic_year;

        const classLevelId =
          formValues.academic_profile
            .class_level;

        if (academicYearId) {
          await loadClasses(
            academicYearId,
          );
        }

        if (
          academicYearId &&
          classLevelId
        ) {
          await loadSections(
            academicYearId,
            classLevelId,
          );
        }

        if (!cancelled) {
          reset(formValues);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorSummary([
            getApiErrorMessage(
              error,
              'Unable to load the student form.',
            ),
          ]);
        }
      } finally {
        if (!cancelled) {
          setPageLoading(false);
        }
      }
    };

    initializePage();

    return () => {
      cancelled = true;
    };
  }, [
    schoolId,
    id,
    isEditMode,
    reset,
    activeAcademicYearId,
  ]);

  const handleClassChange = async (
    event,
  ) => {
    const classLevelId =
      event.target.value;

    setValue(
      'academic_profile.section',
      '',
    );

    clearErrors(
      'academic_profile.section',
    );

    if (classLevelId) {
      try {
        await loadSections(
          selectedAcademicYear,
          classLevelId,
        );
      } catch (error) {
        setErrorSummary([
          getApiErrorMessage(
            error,
            'Unable to load sections.',
          ),
        ]);
      }
    } else {
      setSections([]);
    }
  };

  const getSectionForErrorPath = (
    path,
  ) => {
    if (
      path.startsWith(
        'academic_profile',
      )
    ) {
      return 'academic';
    }

    if (
      path.startsWith(
        'parent_detail',
      )
    ) {
      return 'parent';
    }

    if (
      path.startsWith('addresses')
    ) {
      return 'address';
    }

    if (
      path.startsWith(
        'medical_detail',
      )
    ) {
      return 'medical';
    }

    if (
      path.startsWith(
        'previous_school_detail',
      )
    ) {
      return 'previous';
    }

    if (path.startsWith('docs')) {
      return 'documents';
    }

    return 'student';
  };

  const openErrorSections = (
    fieldErrors,
  ) => {
    const sectionsToOpen = {};

    Object.keys(fieldErrors).forEach(
      (path) => {
        sectionsToOpen[
          getSectionForErrorPath(path)
        ] = true;
      },
    );

    setOpenSections((current) => ({
      ...current,
      ...sectionsToOpen,
    }));
  };

  const scrollToFirstError = (
    fieldErrors,
  ) => {
    const firstPath =
      Object.keys(fieldErrors)[0];

    if (!firstPath) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });

      return;
    }

    window.setTimeout(() => {
      const element =
        document.querySelector(
          `[name="${firstPath}"]`,
        );

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        element.focus();
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const uploadFiles = async (
    studentId,
    values,
  ) => {
    const uploadErrors = [];

    const photoFile = fileFromValue(
      values.docs?.passport_photo,
    );

    if (photoFile) {
      try {
        await studentApi.uploadStudentPhoto(
          schoolId,
          studentId,
          photoFile,
        );
      } catch (error) {
        uploadErrors.push(
          `Student photo: ${getApiErrorMessage(
            error,
            'Upload failed.',
          )}`,
        );
      }
    }

    const documents = [
      {
        value:
          values.docs
            ?.birth_certificate,
        documentType:
          'BIRTH_CERTIFICATE',
        label: 'Birth certificate',
      },
      {
        value:
          values.docs
            ?.transfer_certificate,
        documentType:
          'TRANSFER_CERTIFICATE',
        label:
          'Transfer certificate',
      },
      {
        value:
          values.docs?.id_proof,
        documentType: 'ID_PROOF',
        label: 'ID proof',
      },
    ];

    for (const document of documents) {
      const file = fileFromValue(
        document.value,
      );

      if (!file) {
        continue;
      }

      try {
        await studentApi.uploadStudentDocument(
          schoolId,
          studentId,
          file,
          {
            documentType:
              document.documentType,
          },
        );
      } catch (error) {
        uploadErrors.push(
          `${document.label}: ${getApiErrorMessage(
            error,
            'Upload failed.',
          )}`,
        );
      }
    }

    return uploadErrors;
  };

  const onSubmit = async (values) => {
    clearErrors();
    setErrorSummary([]);
    setSuccessMessage('');
    setWarningMessage('');

    const validationErrors =
      validateStudentForm(values, {
        classLevels,
        sections,
      });

    if (
      Object.keys(validationErrors)
        .length > 0
    ) {
      applyValidationErrors(
        validationErrors,
        setError,
      );

      setErrorSummary(
        validationErrorsToSummary(
          validationErrors,
        ),
      );

      openErrorSections(
        validationErrors,
      );

      scrollToFirstError(
        validationErrors,
      );

      return;
    }

    try {
      const payload =
        buildStudentPayload(values);

      const response = isEditMode
        ? await studentApi.updateStudent(
            schoolId,
            id,
            payload,
          )
        : await studentApi.createStudent(
            schoolId,
            payload,
          );

      const studentId =
        isEditMode
          ? id
          : getStudentIdFromResponse(
              response,
            );

      if (!studentId) {
        throw new Error(
          'Student was saved, but the student ID was not returned.',
        );
      }

      const uploadErrors =
        await uploadFiles(
          studentId,
          values,
        );

      if (uploadErrors.length > 0) {
        setSuccessMessage(
          isEditMode
            ? 'Student details were updated successfully.'
            : 'Student was created successfully.',
        );

        setWarningMessage(
          `Some files were not uploaded: ${uploadErrors.join(
            ' ',
          )}`,
        );

        setOpenSections(
          (current) => ({
            ...current,
            documents: true,
          }),
        );

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });

        return;
      }

      setSuccessMessage(
        isEditMode
          ? 'Student updated successfully.'
          : 'Student created successfully.',
      );

      window.setTimeout(() => {
        navigate(
          `/students/${studentId}`,
        );
      }, 1000);
    } catch (error) {
      const backendErrors =
        getApiFieldErrors(error);

      if (
        Object.keys(backendErrors)
          .length > 0
      ) {
        Object.entries(
          backendErrors,
        ).forEach(
          ([path, message]) => {
            setError(path, {
              type: 'server',
              message,
            });
          },
        );

        setErrorSummary(
          Object.values(
            backendErrors,
          ),
        );

        openErrorSections(
          backendErrors,
        );

        scrollToFirstError(
          backendErrors,
        );
      } else {
        setErrorSummary([
          getApiErrorMessage(
            error,
            'Unable to save the student.',
          ),
        ]);

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
  };

  const sectionErrors = useMemo(
    () => ({
      student: [
        'first_name',
        'middle_name',
        'last_name',
        'gender',
        'date_of_birth',
        'admission_date',
        'admission_no',
        'enrolment_no',
        'aadhaar_no',
        'status',
      ].some((path) =>
        getFieldError(errors, path),
      ),

      academic: Boolean(
        errors.academic_profile,
      ),

      parent: Boolean(
        errors.parent_detail,
      ),

      address: Boolean(
        errors.addresses,
      ),

      medical: Boolean(
        errors.medical_detail,
      ),

      previous: Boolean(
        errors.previous_school_detail,
      ),

      documents: Boolean(
        errors.docs,
      ),
    }),
    [errors],
  );

  if (pageLoading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center">
        <Loader2 className="h-9 w-9 animate-spin text-blue-600" />

        <p className="mt-3 text-sm text-gray-500">
          Loading student details...
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl pb-10">
      <button
        type="button"
        onClick={() =>
          navigate('/students')
        }
        className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-600"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to students
      </button>

      <div className="mb-7 flex flex-col gap-6 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditMode
              ? 'Edit Student'
              : 'Add Student'}
          </h1>

          <p className="mt-2 text-sm text-gray-500">
            Fields marked with * are mandatory.
          </p>
        </div>

        <label
          className="relative flex h-24 w-24 shrink-0 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-gray-300 bg-gray-50 shadow-sm hover:border-blue-400 hover:bg-blue-50"
          title="Upload Student Photo"
        >
          {(() => {
            const photoVal = documentValues?.passport_photo;
            const photoFile = fileFromValue(photoVal);
            let previewUrl = null;
            
            if (typeof photoVal === 'string') {
              previewUrl = photoVal;
            } else if (photoFile && photoFile instanceof File) {
              previewUrl = URL.createObjectURL(photoFile);
            } else if (typeof photoVal?.[0] === 'string') {
              previewUrl = photoVal[0];
            }

            if (previewUrl) {
              return (
                <img
                  src={previewUrl}
                  alt="Student Photo"
                  className="h-full w-full object-cover"
                />
              );
            }

            return (
              <div className="flex flex-col items-center">
                <Upload className="h-6 w-6 text-gray-400" />
                <span className="mt-1 text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                  Photo
                </span>
              </div>
            );
          })()}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            {...register('docs.passport_photo')}
          />
        </label>
      </div>

      {errorSummary.length > 0 && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />

            <div>
              <h2 className="font-semibold text-red-800">
                Student could not be saved
              </h2>

              <ul className="mt-2 space-y-1 text-sm text-red-700">
                {[
                  ...new Set(
                    errorSummary.filter(
                      Boolean,
                    ),
                  ),
                ].map((message) => (
                  <li key={message}>
                    • {message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-5 text-green-700">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
          <p className="text-sm font-medium">
            {successMessage}
          </p>
        </div>
      )}

      {warningMessage && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-800">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <p className="text-sm">
            {warningMessage}
          </p>
        </div>
      )}

      <form
        noValidate
        onSubmit={handleSubmit(
          onSubmit,
        )}
      >
        <AccordionSection
          title="Student Information"
          description="Personal and admission details."
          open={openSections.student}
          onToggle={() =>
            toggleSection('student')
          }
          hasError={
            sectionErrors.student
          }
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            <TextInput
              label="First Name"
              name="first_name"
              required
              register={register}
              errors={errors}
            />

            <TextInput
              label="Middle Name"
              name="middle_name"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Last Name"
              name="last_name"
              register={register}
              errors={errors}
            />

            <SelectInput
              label="Gender"
              name="gender"
              required
              register={register}
              errors={errors}
            >
              <option value="">
                Select gender
              </option>
              <option value="MALE">
                Male
              </option>
              <option value="FEMALE">
                Female
              </option>
              <option value="OTHER">
                Other
              </option>
            </SelectInput>

            <TextInput
              label="Date of Birth"
              name="date_of_birth"
              type="date"
              required
              register={register}
              errors={errors}
            />

            <SelectInput
              label="Blood Group"
              name="blood_group"
              register={register}
              errors={errors}
            >
              <option value="">
                Select blood group
              </option>
              {[
                'A+',
                'A-',
                'B+',
                'B-',
                'AB+',
                'AB-',
                'O+',
                'O-',
              ].map((group) => (
                <option
                  key={group}
                  value={group}
                >
                  {group}
                </option>
              ))}
            </SelectInput>

            <TextInput
              label="Nationality"
              name="nationality"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Religion"
              name="religion"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Caste Category"
              name="caste_category"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Aadhaar Number"
              name="aadhaar_no"
              inputMode="numeric"
              maxLength={12}
              register={register}
              errors={errors}
            />

            <TextInput
              label="Admission Number"
              name="admission_no"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Enrolment Number"
              name="enrolment_no"
              placeholder="Leave blank to auto-generate"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Admission Date"
              name="admission_date"
              type="date"
              required
              register={register}
              errors={errors}
            />

            <SelectInput
              label="Student Status"
              name="status"
              required
              register={register}
              errors={errors}
            >
              <option value="ACTIVE">
                Active
              </option>
              <option value="INACTIVE">
                Inactive
              </option>
              <option value="TRANSFERRED">
                Transferred
              </option>
              <option value="DROPPED">
                Dropped
              </option>
              <option value="GRADUATED">
                Graduated
              </option>
              <option value="ALUMNI">
                Alumni
              </option>
            </SelectInput>
          </div>

          <div className="mt-8 border-t border-gray-200 pt-6">
            <h3 className="mb-4 font-semibold text-gray-900">
              Primary Contact
            </h3>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <TextInput
                label="Primary Contact Name"
                name="parent_detail.primary_contact_name"
                register={register}
                errors={errors}
              />

              <TextInput
                label="Primary Contact Phone"
                name="parent_detail.primary_contact_phone"
                type="tel"
                inputMode="tel"
                maxLength={20}
                register={register}
                errors={errors}
              />
            </div>
          </div>
        </AccordionSection>

        <AccordionSection
          title="Academic Information"
          description="Academic year follows the global working context; then select class and section."
          open={openSections.academic}
          onToggle={() =>
            toggleSection('academic')
          }
          hasError={
            sectionErrors.academic
          }
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Academic Year <RequiredMark />
              </label>
              <input
                type="hidden"
                {...register('academic_profile.academic_year')}
              />
              <div className="mt-1 min-h-[42px] rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-800">
                {selectedAcademicYearName}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {isEditMode &&
                selectedAcademicYear &&
                String(selectedAcademicYear) !== String(activeAcademicYearId)
                  ? 'This existing student record belongs to a different academic year and remains locked.'
                  : 'Controlled by the global working academic year in the sidebar.'}
              </p>
              <FieldError
                message={getFieldError(errors, 'academic_profile.academic_year')}
              />
            </div>

            <SelectInput
              label="Class"
              name="academic_profile.class_level"
              required
              register={register}
              errors={errors}
              disabled={
                !selectedAcademicYear ||
                loadingClasses
              }
              onChange={
                handleClassChange
              }
            >
              <option value="">
                {loadingClasses
                  ? 'Loading classes...'
                  : selectedAcademicYear
                    ? 'Select class'
                    : 'Select year first'}
              </option>

              {classLevels.map(
                (classLevel) => (
                  <option
                    key={classLevel.id}
                    value={classLevel.id}
                  >
                    {classLevel.class_name ||
                      classLevel.name}
                  </option>
                ),
              )}
            </SelectInput>

            <SelectInput
              label="Section"
              name="academic_profile.section"
              required
              register={register}
              errors={errors}
              disabled={
                !selectedClass ||
                loadingSections
              }
            >
              <option value="">
                {loadingSections
                  ? 'Loading sections...'
                  : selectedClass
                    ? 'Select section'
                    : 'Select class first'}
              </option>

              {sections.map(
                (section) => (
                  <option
                    key={section.id}
                    value={section.id}
                  >
                    {section.section_name ||
                      section.name}
                  </option>
                ),
              )}
            </SelectInput>

            <TextInput
              label="Roll Number"
              name="academic_profile.roll_no"
              register={register}
              errors={errors}
            />
          </div>
        </AccordionSection>

        <AccordionSection
          title="Parent and Guardian Details"
          description="At least one complete guardian name and phone number is required."
          open={openSections.parent}
          onToggle={() =>
            toggleSection('parent')
          }
          hasError={
            sectionErrors.parent
          }
        >
          <FieldError
            message={getFieldError(
              errors,
              'parent_detail.root',
            )}
          />

          <div className="mt-3 grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="space-y-5">
              <h3 className="font-semibold text-gray-900">
                Father's Information
              </h3>

              <TextInput
                label="Father's Name"
                name="parent_detail.father_name"
                required
                register={register}
                errors={errors}
              />

              <TextInput
                label="Occupation"
                name="parent_detail.father_occupation"
                register={register}
                errors={errors}
              />

              <TextInput
                label="Mobile Number"
                name="parent_detail.father_phone"
                type="tel"
                inputMode="tel"
                maxLength={20}
                required
                register={register}
                errors={errors}
              />

              <TextInput
                label="Email Address"
                name="parent_detail.father_email"
                type="email"
                register={register}
                errors={errors}
              />
            </div>

            <div className="space-y-5">
              <h3 className="font-semibold text-gray-900">
                Mother's Information
              </h3>

              <TextInput
                label="Mother's Name"
                name="parent_detail.mother_name"
                required
                register={register}
                errors={errors}
              />

              <TextInput
                label="Occupation"
                name="parent_detail.mother_occupation"
                register={register}
                errors={errors}
              />

              <TextInput
                label="Mobile Number"
                name="parent_detail.mother_phone"
                type="tel"
                inputMode="tel"
                maxLength={20}
                required
                register={register}
                errors={errors}
              />

              <TextInput
                label="Email Address"
                name="parent_detail.mother_email"
                type="email"
                register={register}
                errors={errors}
              />
            </div>
          </div>
        </AccordionSection>

        <AccordionSection
          title="Address Details"
          description="Current address is mandatory. Permanent address is optional."
          open={openSections.address}
          onToggle={() =>
            toggleSection('address')
          }
          hasError={
            sectionErrors.address
          }
        >
          <div className="space-y-8">
            <div>
              <h3 className="mb-4 font-semibold text-gray-900">
                Current Address
              </h3>

              <input
                type="hidden"
                value="CURRENT"
                {...register(
                  'addresses.0.address_type',
                )}
              />

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <TextInput
                  label="Address Line 1"
                  name="addresses.0.address_line1"
                  required
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="Address Line 2"
                  name="addresses.0.address_line2"
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="City"
                  name="addresses.0.city"
                  required
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="State"
                  name="addresses.0.state"
                  required
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="Country"
                  name="addresses.0.country"
                  required
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="Pincode"
                  name="addresses.0.pincode"
                  required
                  inputMode="numeric"
                  maxLength={6}
                  register={register}
                  errors={errors}
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-7">
              <h3 className="mb-4 font-semibold text-gray-900">
                Permanent Address
              </h3>

              <input
                type="hidden"
                value="PERMANENT"
                {...register(
                  'addresses.1.address_type',
                )}
              />

              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <TextInput
                  label="Address Line 1"
                  name="addresses.1.address_line1"
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="Address Line 2"
                  name="addresses.1.address_line2"
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="City"
                  name="addresses.1.city"
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="State"
                  name="addresses.1.state"
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="Country"
                  name="addresses.1.country"
                  register={register}
                  errors={errors}
                />

                <TextInput
                  label="Pincode"
                  name="addresses.1.pincode"
                  inputMode="numeric"
                  maxLength={6}
                  register={register}
                  errors={errors}
                />
              </div>
            </div>
          </div>
        </AccordionSection>

        <AccordionSection
          title="Medical Details"
          description="Optional medical and emergency information."
          open={openSections.medical}
          onToggle={() =>
            toggleSection('medical')
          }
          hasError={
            sectionErrors.medical
          }
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextArea
              label="Allergies"
              name="medical_detail.allergies"
              register={register}
              errors={errors}
            />

            <TextArea
              label="Medical Conditions"
              name="medical_detail.medical_conditions"
              register={register}
              errors={errors}
            />

            <TextArea
              label="Regular Medication"
              name="medical_detail.regular_medication"
              register={register}
              errors={errors}
            />

            <TextArea
              label="Emergency Instructions"
              name="medical_detail.emergency_instructions"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Doctor Name"
              name="medical_detail.doctor_name"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Doctor Phone"
              name="medical_detail.doctor_phone"
              type="tel"
              inputMode="tel"
              maxLength={20}
              register={register}
              errors={errors}
            />
          </div>
        </AccordionSection>

        <AccordionSection
          title="Previous School Information"
          description="Optional details from the previous school."
          open={openSections.previous}
          onToggle={() =>
            toggleSection('previous')
          }
          hasError={
            sectionErrors.previous
          }
        >
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <TextInput
              label="Previous School Name"
              name="previous_school_detail.previous_school_name"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Previous Class"
              name="previous_school_detail.previous_class"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Transfer Certificate Number"
              name="previous_school_detail.transfer_certificate_no"
              register={register}
              errors={errors}
            />

            <TextInput
              label="Last Attended Date"
              name="previous_school_detail.last_attended_date"
              type="date"
              register={register}
              errors={errors}
            />

            <div className="md:col-span-2">
              <TextArea
                label="Reason for Leaving"
                name="previous_school_detail.reason_for_leaving"
                register={register}
                errors={errors}
              />
            </div>
          </div>
        </AccordionSection>

        <AccordionSection
          title="Documents and Photo"
          description="Upload new files. Existing files are not removed during editing."
          open={openSections.documents}
          onToggle={() =>
            toggleSection('documents')
          }
          hasError={
            sectionErrors.documents
          }
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {[
              {
                name:
                  'docs.birth_certificate',
                label:
                  'Birth Certificate',
                value:
                  documentValues
                    ?.birth_certificate,
                accept:
                  '.pdf,image/jpeg,image/png,image/webp',
              },
              {
                name:
                  'docs.transfer_certificate',
                label:
                  'Transfer Certificate',
                value:
                  documentValues
                    ?.transfer_certificate,
                accept:
                  '.pdf,image/jpeg,image/png,image/webp',
              },
              {
                name: 'docs.id_proof',
                label: 'ID Proof',
                value:
                  documentValues?.id_proof,
                accept:
                  '.pdf,image/jpeg,image/png,image/webp',
              },
              {
                name:
                  'docs.caste_certificate',
                label: 'Caste Certificate',
                value:
                  documentValues
                    ?.caste_certificate,
                accept:
                  '.pdf,image/jpeg,image/png,image/webp',
              },
            ].map((document) => {
              const selectedFile =
                fileFromValue(
                  document.value,
                );

              return (
                <label
                  key={document.name}
                  className="relative flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-5 text-center hover:border-blue-400 hover:bg-blue-50"
                >
                  <Upload className="h-6 w-6 text-gray-400" />

                  <span className="mt-2 text-sm font-semibold text-gray-800">
                    {document.label}
                  </span>

                  <span className="mt-1 max-w-full truncate text-xs text-gray-500">
                    {selectedFile?.name ||
                      'Choose file'}
                  </span>

                  <input
                    type="file"
                    accept={
                      document.accept
                    }
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    {...register(
                      document.name,
                    )}
                  />
                </label>
              );
            })}
          </div>
        </AccordionSection>

        <div className="mt-8 flex flex-col justify-end gap-3 sm:flex-row">
          <button
            type="button"
            disabled={isSubmitting}
            onClick={() =>
              navigate('/students')
            }
            className="rounded-xl border border-gray-300 px-6 py-3 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-7 py-3 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting && (
              <Loader2 className="h-5 w-5 animate-spin" />
            )}

            {isSubmitting
              ? 'Saving student...'
              : isEditMode
                ? 'Update Student'
                : 'Save Student'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddStudentPage;
